import React, { useRef, useState } from "react";
import { downloadExcelTemplate } from "../../../../core/excelDownloadTemplate/downloadExcelTemplate";
import { useAuth } from "../../../../core/context/useAuthContext";
import useApi from "../../../../core/customhook/useApi";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";

const MAX_FILE_SIZE_MB = 10;

const AddBulkLob = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();

  const { auth } = useAuth();
  const orgId = auth?.orgId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const [templateDownloaded, setTemplateDownloaded] = useState(false);

  const bulkUploadLob = useApi<any>(
    `/api/v1/master/organizations/${orgId}/bulk-upload`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const isUploading = bulkUploadLob.loading;
  // ========== here is all excel upload state and funcation ===============

  const [readingExcel, setReadingExcel] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [excelRows, setExcelRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [validRows, setValidRows] = useState(0);
  const [invalidRows, setInvalidRows] = useState(0);
  const [sheetName, setSheetName] = useState("");

  const REQUIRED_HEADERS = [
    "Account Code *",
    // "Account Name *",
    // "Brand Name *",
    // "Legal Name *",
    // "Headquarter Address *",
    // "Country *",
    // "Account Manager Name *",
    // "Manager Email *",
    "Program Code *",
    "Program Name *",
    "Program Description *",
    "Location *",
    "Department Code *",
    "Department Name *",
    "Department Description *",
  ];

  const readExcel = async (selectedFile: File) => {
    try {
      setReadingExcel(true);
      setUploadProgress(0);
      setFileError("");

      const data = await selectedFile.arrayBuffer();
      setUploadProgress(20);

      const workbook = XLSX.read(data);
      setUploadProgress(40);

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      setSheetName(workbook.SheetNames[0]);

      /* ---------------- HEADER VALIDATION ---------------- */

      const headerData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      });

      const excelHeaders = (headerData[0] || []) as string[];

      const missingHeaders = REQUIRED_HEADERS.filter(
        (header) => !excelHeaders.includes(header),
      );

      if (missingHeaders.length > 0) {
        setReadingExcel(false);

        setFileError(
          `Invalid template. Missing/Changed column(s): ${missingHeaders.join(", ")}`,
        );

        setFile(null);
        setExcelRows([]);
        setTotalRows(0);
        setValidRows(0);
        setInvalidRows(0);

        return;
      }

      /* ---------------- READ DATA ---------------- */

      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
      });

      setUploadProgress(60);

      const filteredRows = jsonData.filter((row) =>
        Object.values(row).some((value) => String(value).trim() !== ""),
      );

      setTotalRows(filteredRows.length);

      /* ---------------- ROW VALIDATION ---------------- */

      const validPrograms: any[] = [];
      const invalidRowsList: any[] = [];

      filteredRows.forEach((row: any, index: number) => {
        const errors: string[] = [];

        if (!row["Department Code *"]?.toString().trim()) {
          errors.push("Department Code");
        }

        if (!row["Department Name *"]?.toString().trim()) {
          errors.push("Department Name");
        }

        if (!row["Department Description *"]?.toString().trim()) {
          errors.push("Department Description");
        }

        if (!row["Program Code *"]?.toString().trim()) {
          errors.push("Program Code");
        }

        if (!row["Program Name *"]?.toString().trim()) {
          errors.push("Program Name");
        }

        if (!row["Program Description *"]?.toString().trim()) {
          errors.push("Program Description");
        }

        if (!row["Location *"]?.toString().trim()) {
          errors.push("Location");
        }

        if (!row["Account Code *"]?.toString().trim()) {
          errors.push("Account Code");
        }

        const email = row["Manager Email *"]?.toString().trim();

        if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
          errors.push("Manager Email (Invalid)");
        }

        if (errors.length > 0) {
          invalidRowsList.push({
            rowNumber: index + 2,
            errors,
          });

          return;
        }

        validPrograms.push({
          department_code: row["Department Code *"].toString().trim(),
          department_name: row["Department Name *"].toString().trim(),
          department_description: row["Department Description *"]
            .toString()
            .trim(),

          program_code: row["Program Code *"].toString().trim(),
          program_name: row["Program Name *"].toString().trim(),
          program_description: row["Program Description *"].toString().trim(),

          location_name: row["Location *"].toString().trim(),

          project_code: row["Account Code *"].toString().trim(),
        });
      });

      setExcelRows(validPrograms);

      setValidRows(validPrograms.length);
      setInvalidRows(invalidRowsList.length);
      setUploadProgress(100);

      setTimeout(() => {
        setReadingExcel(false);
      }, 300);
    } catch (error) {
      setReadingExcel(false);

      setFileError("Unable to read Excel file.");
    }
  };

  const handleDownloadTemplate = () => {
    downloadExcelTemplate({
      fileName: "Bulk_Hierarchy_Upload_Report.xlsx",
      sheetName: "Departments",
      columns: [
        { header: "Department Code *", key: "department_code", width: 25 },
        { header: "Department Name *", key: "department_name", width: 35 },
        {
          header: "Department Description *",
          key: "department_description",
          width: 45,
        },
        { header: "Program Code *", key: "program_code", width: 25 },
        { header: "Program Name *", key: "program_name", width: 35 },
        {
          header: "Program Description *",
          key: "program_description",
          width: 45,
        },
        { header: "Location *", key: "location_name", width: 25 },
        { header: "Account Code *", key: "account_code", width: 25 },
      ],
      // 👇 dummy data for checking only — remove before going live
      data: [
        {
          department_code: "CORG_LOB_2574",
          department_name: "Adobe-Adobe-Care-Inbound Call B2C",
          department_description:
            "Adobe-Adobe-Care-Inbound Call--Business-to-Consumer (B2C)-Live Agent in SITE-Offshore-TPIN 398 Gurugram",
          program_code: "GGN 398",
          program_name: "Adobe Gurgaon",
          program_description: "Adobe Gurgaon Call Center",
          location_name: "Gurgaon",
          account_code: "ADOBE_IN",
        },
        {
          department_code: "CORG_LOB_8025",
          department_name: "Technical/Product support-Inbound Call-TS",
          department_description:
            "Adobe-Adobe-Technical/Product support-Inbound Call-TS Help Desk-Business-to-Business (B2B)-Live Agent in SITE-Offshore-TPIN 398 Gurugram",
          program_code: "",
          program_name: "",
          program_description: "",
          location_name: "",
          account_code: "",
        },
        {
          department_code: "CORG_LOB_13990",
          department_name: "Chat-Adobe-Technical/Product support-Chat",
          department_description:
            "Adobe-Chat-Adobe-Technical/Product support-Chat-TS Help Desk-Business-to-Consumer (B2C)-Live Agent in SITE-Offshore",
          program_code: "",
          program_name: "",
          program_description: "",
          location_name: "",
          account_code: "",
        },
        {
          department_code: "CORG_LOB_15184",
          department_name: "DX WFM-Adobe-Data Analytics-Outbound",
          department_description:
            "Adobe-DX WFM-Adobe-Data Analytics-Outbound Call-DA Other-Business-to-Employees (B2E)-Live Agent in SITE-Offshore-TPIN 398 Gurugram",
          program_code: "",
          program_name: "",
          program_description: "",
          location_name: "",
          account_code: "",
        },
        {
          department_code: "CORG_LOB_23153",
          department_name: "Adobe India CHRO-Adobe-Other-Other-OTH",
          department_description:
            "Adobe India CHRO-Adobe-Other-Other-OTH Other-Business-to-Consumer (B2C)-Live Agent in SITE-Offshore-XX TPIN ADOBE SYSTEMS INDIA-NOIDA",
          program_code: "Noida",
          program_name: "Abobe Noida",
          program_description: "Adobe Noida Call Center",
          location_name: "Noida",
          account_code: "",
        },
        {
          department_code: "CORG_LOB_23269",
          department_name: "Technology Products & Services-Inbound Call",
          department_description:
            "Adobe Systems India Private Limited-Tech revenue-Adobe-Technology Products & Services-Inbound Call-TS Other-Business-to-Consumer (B2C)-Live Agent in SITE-Offshore-XX TPIN ADOBE SYSTEMS INDIA-NOIDA",
          program_code: "",
          program_name: "",
          program_description: "",
          location_name: "",
          account_code: "",
        },
      ],
    });
  };
  const processFile = async (selectedFile: File) => {
    setFileError("");

    if (!selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      setFileError("Please upload a valid .xlsx file.");
      return;
    }

    setFile(selectedFile);

    await readExcel(selectedFile);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    await processFile(selectedFile);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];

    if (!droppedFile) return;

    await processFile(droppedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError(null);
    // setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleBulkUpload = async () => {
    if (excelRows.length === 0) {
      return Swal.fire({
        icon: "warning",
        title: "Excel Required",
        text: "Please upload a valid Excel file.",
      });
    }

    try {
      const payload = {
        rows: excelRows,
      };
      const res = await bulkUploadLob.callApi(payload);
      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.message,
        });

        setFile(null);
        setExcelRows([]);
        setTotalRows(0);
        setValidRows(0);
        setInvalidRows(0);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to upload hierarchy.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Add Hierarchy</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Add Hierarchy</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white">
            <h4 className="mb-0 fw-bold">Bulk Hierarchy Upload</h4>
            <small className="text-muted">
              Upload multiple hierarchy records using a single Excel file.
            </small>
          </div>

          <div className="card-body">
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <i
                  className="ti ti-info-circle-filled me-2"
                  style={{ color: "#d2660d", fontSize: "20px" }}
                />
                <h6 className="mb-0 fw-bold">Bulk Upload Instructions</h6>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {[
                  "Use the latest Excel template.",
                  "Do not modify the column headers.",
                  "Only .xlsx files are supported.",
                  "Complete all required fields.",
                  "Do not leave any blank rows in the worksheet.",
                ].map((item, index) => (
                  <span
                    key={index}
                    className="px-2 py-2 rounded-pill"
                    style={{
                      background: "#fdeaea",
                      borderColor: "#dc3545",
                      color: "#b02a37",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    <i className="ti ti-check me-1"></i>
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <hr
              style={{
                border: "none",
                height: "2px",
                background:
                  "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                borderRadius: "999px",
                opacity: 1,
              }}
            />

            {/* STEP 1 */}
            <div className="p-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                  style={{ width: 45, height: 45 }}
                >
                  1
                </div>
                <div>
                  <h5 className="mb-1">Download Sample Template</h5>
                  <small className="text-muted">
                    Download the latest Excel template and enter your hierarchy
                    details. Keep the original column headers unchanged to
                    ensure successful data processing.
                  </small>
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleDownloadTemplate}
              >
                <i className="ti ti-download me-2"></i>
                Download Template
              </button>
            </div>

            <hr
              style={{
                border: "none",
                height: "2px",
                background:
                  "linear-gradient(to right, transparent, #6d3300, #af5d00, #f26522, #f26522, #6d3300, transparent)",
                borderRadius: "999px",
                opacity: 1,
              }}
            />
            {/* STEP 2 */}
            <div className="p-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center bg-primary text-white justify-content-center me-3"
                  style={{ width: 45, height: 45 }}
                >
                  2
                </div>
                <div>
                  <h5 className="mb-1">Upload Completed Excel File</h5>
                  <small className="text-muted"> 
                    Upload the completed .xlsx template. Maximum file size: {MAX_FILE_SIZE_MB} MB. Upload one file at a time.
                  </small>
                </div>
              </div>

              {!file && (
                <div
                  className="border border-2 rounded text-center p-5"
                  style={{
                    borderStyle: "dashed",
                    borderColor: isDragging ? "#f26522" : undefined,
                    backgroundColor: isDragging
                      ? "rgba(13,110,253,0.05)"
                      : undefined,
                    cursor: "pointer",
                    transition: "all .15s ease-in-out",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      fileInputRef.current?.click();
                  }}
                >
                  <i
                    className="ti ti-file-spreadsheet"
                    style={{ fontSize: 55, color: "#f26522" }}
                  />
                  <h6 className="mt-3">Drag and drop your Excel file here</h6>
                  <p className="text-muted mb-3">
                    or{" "}
                    <span className="text-primary text-decoration-underline">
                      browse your computer
                    </span>
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {/* Reading Loader */}

              {readingExcel && (
                <div className="card mt-3 border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary me-3"
                        role="status"
                      />

                      <div>
                        <h6 className="mb-1">Reading Excel File...</h6>

                        <small className="text-muted">
                          Please wait while we validate your spreadsheet.
                        </small>
                      </div>
                    </div>

                    <div className="progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{
                          width: `${uploadProgress}%`,
                        }}
                      />
                    </div>

                    <div className="text-end mt-2">
                      <small className="text-muted">
                        {uploadProgress}% Completed
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* File Details */}

              {!readingExcel && file && (
                <div className="card mt-3 border">
                  <div className="card-body py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded bg-success-subtle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: 48,
                            height: 48,
                          }}
                        >
                          <i
                            className="ti ti-file-spreadsheet text-success"
                            style={{
                              fontSize: 28,
                            }}
                          />
                        </div>

                        <div>
                          <h6 className="mb-1">{file.name}</h6>

                          <small className="text-muted">
                            {(file.size / 1024).toFixed(1)} KB
                          </small>
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleRemoveFile}
                      >
                        <i className="ti ti-trash me-1"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Excel Summary */}

              {!readingExcel && excelRows.length > 0 && (
                <div className="card mt-3 border">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="ti ti-table me-2"></i>
                      Excel Summary
                    </h6>
                  </div>

                  <div className="card-body">
                    <div className="row text-center">
                      <div className="col-md-3">
                        <small className="text-muted d-block">Worksheet</small>

                        <strong>{sheetName}</strong>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">Total Rows</small>

                        <p className="badge bg-primary">{totalRows}</p>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">Valid Rows</small>

                        <p className="badge bg-success">{validRows}</p>
                      </div>

                      <div className="col-md-3">
                        <small className="text-muted d-block">
                          Invalid Rows
                        </small>

                        <p className="badge bg-danger">{invalidRows}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}

              {fileError && (
                <div className="alert alert-danger mt-3 mb-0">
                  <i className="ti ti-alert-circle me-2"></i>

                  {fileError}
                </div>
              )}
            </div>

            {/* button */}
            <div className="p-4">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary btn-lg"
                  disabled={!file || excelRows.length === 0}
                  onClick={handleBulkUpload}
                >
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload me-2"></i>
                      Upload Hierarchy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBulkLob;
